import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { DashboardService, DashboardSnapshot } from './dashboard.service';

interface Kpi {
  label: string;
  value: number | string;
  meta: string;
  route: string;
  queryParams?: any;
  dark?: boolean;
  tone?: 'ok' | 'warn' | 'danger';
}

interface Attention {
  title: string;
  meta: string;
  badge: string;
  tone: 'primary' | 'warn' | 'danger' | 'neutral';
  route: string;
  queryParams?: any;
}

interface PipelineStage {
  label: string;
  value: number;
  meta: string;
  tone: 'plain' | 'warn' | 'alert';
  route: string;
}

/**
 * Operations overview — the landing page. Everything on it is live data for
 * the store/warehouse the user is working in; tiles whose module the user
 * cannot access are simply not shown.
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  private dashboard = inject(DashboardService);
  private authService = inject(AuthService);

  loading = signal(true);
  loadedAt = signal<Date | null>(null);
  snap = signal<DashboardSnapshot | null>(null);

  locationName = signal<string | null>(this.readLocationName());
  locationType = signal<string | null>(this.authService.getActiveLocationType());
  userName = signal<string>(this.readUserName());

  greeting = computed(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  });

  kpis = computed<Kpi[]>(() => {
    const s = this.snap();
    if (!s) return [];
    const out: Kpi[] = [];
    if (s.activePos) {
      out.push({
        label: 'Open purchase orders',
        value: s.activePos.total,
        meta: s.draftPos != null ? `${s.draftPos} in draft` : 'active',
        route: '/po',
        dark: true,
      });
    }
    if (s.grns) {
      out.push({
        label: 'Goods receipts',
        value: s.grns.total,
        meta: s.draftGrns != null ? `${s.draftGrns} draft${s.draftGrns === 1 ? '' : 's'} open` : 'all time',
        route: '/grn',
        tone: s.draftGrns ? 'warn' : undefined,
      });
    }
    if (s.outboundInTransit != null) {
      out.push({
        label: 'Transfers in transit',
        value: s.outboundInTransit,
        meta: s.draftTransfers != null ? `${s.draftTransfers} draft${s.draftTransfers === 1 ? '' : 's'}` : 'outbound',
        route: '/transfer-out',
      });
    }
    if (s.inboundInTransit != null) {
      out.push({
        label: 'Awaiting receipt',
        value: s.inboundInTransit,
        meta: 'inbound shipments',
        route: '/transfer-in',
        tone: s.inboundInTransit ? 'warn' : undefined,
      });
    }
    if (s.lowStock) {
      out.push({
        label: 'Below reorder level',
        value: s.lowStock.length,
        meta: 'SKUs at this location',
        route: '/reorders/low-stock',
        tone: s.lowStock.length ? 'danger' : 'ok',
      });
    }
    if (s.stockSummary) {
      out.push({
        label: 'Stock on hand',
        value: this.fmtQty(s.stockSummary.onHandQty),
        meta: this.stockMeta(s.stockSummary),
        route: '/inventory/list',
      });
    }
    return out;
  });

  pipeline = computed<PipelineStage[]>(() => {
    const s = this.snap();
    if (!s) return [];
    const stages: PipelineStage[] = [];
    if (s.draftPos != null) stages.push({ label: 'PO drafts', value: s.draftPos, meta: 'not yet posted', tone: 'plain', route: '/po' });
    if (s.activePos) stages.push({ label: 'POs active', value: s.activePos.total, meta: s.expiredPosWithoutGrn ? `${s.expiredPosWithoutGrn} expired without GRN` : 'awaiting goods', tone: s.expiredPosWithoutGrn ? 'warn' : 'plain', route: '/po' });
    if (s.draftGrns != null) stages.push({ label: 'GRN in progress', value: s.draftGrns, meta: 'draft receipts', tone: s.draftGrns ? 'alert' : 'plain', route: '/grn' });
    if (s.outboundInTransit != null) stages.push({ label: 'In transit', value: s.outboundInTransit, meta: 'shipped to stores', tone: 'plain', route: '/transfer-out' });
    if (s.inboundInTransit != null) stages.push({ label: 'To receive', value: s.inboundInTransit, meta: 'arriving here', tone: s.inboundInTransit ? 'warn' : 'plain', route: '/transfer-in' });
    return stages;
  });

  attention = computed<Attention[]>(() => {
    const s = this.snap();
    if (!s) return [];
    const list: Attention[] = [];
    if (s.expiredPosWithoutGrn) {
      list.push({ title: `${s.expiredPosWithoutGrn} purchase order${s.expiredPosWithoutGrn === 1 ? '' : 's'} expired without a GRN`, meta: 'Recall or close them so the open-PO list stays honest', badge: 'FOLLOW UP', tone: 'danger', route: '/po' });
    }
    if (s.draftGrns) {
      list.push({ title: `${s.draftGrns} goods receipt${s.draftGrns === 1 ? '' : 's'} still in draft`, meta: 'Stock is not updated until the GRN is posted', badge: 'POST', tone: 'warn', route: '/grn' });
    }
    if (s.inboundInTransit) {
      list.push({ title: `${s.inboundInTransit} transfer${s.inboundInTransit === 1 ? '' : 's'} waiting to be received`, meta: 'Shipped to this location and not yet counted in', badge: 'RECEIVE', tone: 'primary', route: '/transfer-in' });
    }
    if (s.lowStock?.length) {
      list.push({ title: `${s.lowStock.length} SKU${s.lowStock.length === 1 ? '' : 's'} below reorder level`, meta: 'Raise an indent or purchase order before they run out', badge: 'REORDER', tone: 'warn', route: '/reorders/low-stock' });
    }
    if (s.returnsInbox?.PENDING_APPROVAL) {
      list.push({ title: `${s.returnsInbox.PENDING_APPROVAL} customer return${s.returnsInbox.PENDING_APPROVAL === 1 ? '' : 's'} awaiting approval`, meta: 'Refunds are held until a manager approves', badge: 'APPROVE', tone: 'danger', route: '/returns' });
    }
    if (s.returnsInbox?.REQUIRES_RECONCILIATION) {
      list.push({ title: `${s.returnsInbox.REQUIRES_RECONCILIATION} return${s.returnsInbox.REQUIRES_RECONCILIATION === 1 ? '' : 's'} need reconciliation`, meta: 'Cash drawer and refund records differ', badge: 'RECONCILE', tone: 'warn', route: '/returns' });
    }
    if (s.stockTakesInProgress) {
      list.push({ title: `${s.stockTakesInProgress} stock take session${s.stockTakesInProgress === 1 ? '' : 's'} in progress`, meta: 'Merge device counts and complete to apply variances', badge: 'REVIEW', tone: 'neutral', route: '/stocktake' });
    }
    if (s.draftTransfers) {
      list.push({ title: `${s.draftTransfers} transfer out draft${s.draftTransfers === 1 ? '' : 's'}`, meta: 'Dispatch or discard drafts that are no longer needed', badge: 'DISPATCH', tone: 'neutral', route: '/transfer-out' });
    }
    return list;
  });

  recentPos = computed(() => this.snap()?.activePos?.data ?? []);
  recentGrns = computed(() => this.snap()?.grns?.data ?? []);
  stock = computed(() => this.snap()?.stockSummary ?? null);

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.dashboard.load(this.authService.getActiveLocationId()).subscribe({
      next: (snap) => {
        this.snap.set(snap);
        this.loadedAt.set(new Date());
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  poTone(status: string): string {
    return status === 'POSTED' ? 'ok' : status === 'DRAFT' ? 'warn' : status === 'CANCELLED' ? 'danger' : '';
  }

  fmtQty(v: any): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return '0';
    if (Math.abs(n) >= 100000) return (n / 100000).toFixed(1) + 'L';
    if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + 'k';
    return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  private stockMeta(summary: any): string {
    if (summary?.location?.type === 'Warehouse') {
      return `${this.fmtQty(summary.inTransitOutboundQty)} in transit to stores`;
    }
    return `${this.fmtQty(summary?.inTransitInboundQty)} in transit from warehouse`;
  }

  /** Name of the store/warehouse being worked in (same source as the top-bar chip). */
  private readLocationName(): string | null {
    const id = this.authService.getActiveLocationId();
    const access = this.authService.getStoreAccess().find((a) => a.locationId === id);
    return access?.locationName || access?.storeName || localStorage.getItem('PrimaryLocationName');
  }

  private readUserName(): string {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return (u?.name || u?.username || '').split(' ')[0];
    } catch {
      return '';
    }
  }
}
